import React, { useState, useCallback, useRef } from 'react';
// Using a custom horizontal scroller with snap paging
import { YStack, useTheme, Dialog, Button } from 'tamagui';
import CloseIcon from '@mui/icons-material/Close';
// import { ImageZoom } from '@likashefqet/react-native-image-zoom';
// import { GestureHandlerRootView } from 'react-native-gesture-handler';

import { CobuText } from '@cobu/ui';
import { CommentImage } from '@cobu/types';
import { Strings } from '../../constants/Strings';

interface ImageViewerModalProps {
  visible: boolean;
  images: CommentImage[];
  initialIndex?: number;
  onClose: () => void;
}

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: '#000000',
    width: '100vw',
    height: '100vh',
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    paddingTop: 40,
    paddingHorizontal: 16,
  },
  headerContent: {
    width: '100%',
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 36,
    right: 16,
    borderRadius: 20,
  },
  imageContainer: {
    width: '100vw',
    height: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageZoom: {
    width: '80vw',
    height: '80vh',
    objectFit: 'contain',
  },
} as const;

export default function ImageViewerModal({
  visible,
  images,
  initialIndex = 0,
  onClose,
}: ImageViewerModalProps) {
  const theme = useTheme();
  const listRef = useRef<HTMLDivElement | null>(null);
  // Ensure the initial index is always within bounds
  const safeInitialIndex = React.useMemo(() => {
    if (!images || images.length === 0) return 0;
    return Math.min(Math.max(initialIndex, 0), images.length - 1);
  }, [images, initialIndex]);

  const [currentIndex, setCurrentIndex] = useState(safeInitialIndex);

  // Reset and programmatically scroll to the requested index when modal opens
  React.useEffect(() => {
    if (!visible) return;
    if (safeInitialIndex !== currentIndex) {
      setCurrentIndex(safeInitialIndex);
    }
    requestAnimationFrame(() => {
      try {
        const el = listRef.current;
        if (!el) return;
        const targetLeft = el.clientWidth * safeInitialIndex;
        el.scrollTo({ left: targetLeft, behavior: 'auto' });
      } catch (_) {}
    });
  }, [visible, safeInitialIndex]);

  // Render individual image item
  const renderImageItem = useCallback(({ item, index }: { item: CommentImage; index: number }) => {
    // Handle both 'url' and legacy 'image' field names
    const imageUrl = item.url || (item as any).image;

    return (
      <YStack
        style={styles.imageContainer}
        background="$black"
      >
        <img
          src={imageUrl}
          style={styles.imageZoom}
          // resizeMode="contain"
          // minScale={1}
          // maxScale={5}
          // doubleTapScale={3}
          // isDoubleTapEnabled
        />
      </YStack>
    );
  }, []);

  // Update current index from horizontal scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    if (!Number.isNaN(index)) {
      const clamped = Math.min(Math.max(index, 0), Math.max(0, images.length - 1));
      if (clamped !== currentIndex) setCurrentIndex(clamped);
    }
  }, [images.length, currentIndex]);

  if (!visible) return null;

  return (
    <Dialog open={visible} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          style={{ width: '100vw', height: '100vh', backgroundColor: 'transparent', padding: 0 }}
        >
          {/* <GestureHandlerRootView style={{ flex: 1 }}> */}
          <YStack style={styles.safeArea}>
            {/* Header with close button and counter */}
            <YStack style={{ ...styles.headerOverlay, pointerEvents: 'none' }}>
              {/* Close button pinned top-right */}
              <Button
                onPress={onClose}
                style={{
                  ...(styles.closeButton as any),
                  padding: 12,
                  pointerEvents: 'auto',
                  zIndex: 101,
                }}
                backgroundColor="$pageBackground"
                size="$6"
                circular
                hitSlop={12}
                aria-label={Strings.get().ui.imageViewer.closeLabel}
              >
                <CloseIcon name="close" width={24} height={24} style={{ backgroundColor: "$white" }} />
              </Button>

              {/* Counter centered */}
              <YStack style={{ ...styles.headerContent, pointerEvents: 'auto' }}>
                {images.length > 1 && (
                  <CobuText variant="body" color="$white" fontSize="$3">
                    {currentIndex + 1} / {images.length}
                  </CobuText>
                )}
              </YStack>
            </YStack>


            {/* Image gallery */}
            <YStack f={1} background="$black">
              <div
                ref={listRef}
                onScroll={handleScroll}
                style={{
                  height: '100%',
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  display: 'flex',
                  flexDirection: 'row',
                  scrollSnapType: 'x mandatory',
                }}
              >
                {images.map((img, index) => (
                  <YStack
                    key={(img as any).uuid || String(index)}
                    style={{ ...(styles.imageContainer as any), scrollSnapAlign: 'start' as any }}
                    background="$black"
                  >
                    {renderImageItem({ item: img, index })}
                  </YStack>
                ))}
              </div>
            </YStack>
          </YStack>
          {/* </GestureHandlerRootView> */}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
} 